/**
 * Espace Famille — service worker et parcours d'installation (#2541).
 * Chargé sous /parent via layouts/pwa-head.blade.php.
 *
 * ⚠ Le bandeau vit dans #app, sur lequel Vue est monté : Vue remplace ce DOM
 * à son rendu. D'où la visibilité portée par une classe sur <html> plutôt que
 * par un attribut, et les clics délégués au document plutôt qu'aux éléments.
 */

import { meta, reportAppState } from './parent-app-state';

const INSTALLED_KEY = 'moser-famille-installed';

// Le signalement d'installation ne vaut qu'une fois par appareil : le serveur
// horodate la première occurrence, inutile de la lui répéter à chaque page.
const INSTALL_REPORTED_KEY = 'moser-famille-installed-reported';
const ANDROID_CLASS = 'pwa-can-install';
const IOS_CLASS = 'pwa-ios-hint';

let deferredPrompt = null;
let alreadyInstalled = false;

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

// Safari iOS n'implémente pas `beforeinstallprompt` : l'installation y passe
// par le menu de partage, d'où une consigne au lieu d'un bouton. Les autres
// navigateurs iOS ne savent pas installer et ont un menu différent.
function isIosSafari() {
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua)
        || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);

    return isIos && isSafari;
}

function read(key) {
    try {
        return window.localStorage.getItem(key) === '1';
    } catch (e) {
        return false;
    }
}

function write(key, value) {
    try {
        if (value) {
            window.localStorage.setItem(key, '1');
        } else {
            window.localStorage.removeItem(key);
        }
    } catch (e) {
        // Sans stockage, l'état ne tient que le temps de la session.
    }
}

// Le refus vit en base et non plus dans le stockage local : au chargement
// suivant, le serveur rendra le bandeau en pied de page au lieu du haut.
function dismiss() {
    reportAppState('install_banner_dismissed');
}

// Marqueur distinct du refus : « déjà installée » et « n'en veut pas » sont
// deux états différents.
function markInstalled(installed) {
    alreadyInstalled = installed;
    write(INSTALLED_KEY, installed);

    if (installed && !read(INSTALL_REPORTED_KEY)) {
        write(INSTALL_REPORTED_KEY, true);
        reportAppState('pwa_installed');
    }
}

function canOffer() {
    return !alreadyInstalled && !read(INSTALLED_KEY) && !isStandalone();
}

function showBanner(variantClass) {
    if (canOffer()) {
        document.documentElement.classList.add(variantClass);
    }
}

function hideBanner() {
    document.documentElement.classList.remove(ANDROID_CLASS, IOS_CLASS);
}

// Seule façon de savoir depuis un onglet que la PWA est installée. Suppose
// `related_applications` dans le manifest, et un vrai WebAPK sur Android :
// une installation retombée en simple raccourci est lue comme absente.
function syncInstalledState() {
    if (!navigator.getInstalledRelatedApps) {
        return;
    }

    navigator.getInstalledRelatedApps()
        .then((apps) => {
            markInstalled(apps.length > 0);

            if (apps.length > 0) {
                hideBanner();
            } else if (deferredPrompt) {
                showBanner(ANDROID_CLASS);
            } else if (isIosSafari()) {
                showBanner(IOS_CLASS);
            }
        })
        .catch(() => {});
}

// Un téléphone familial est partagé : la déconnexion doit effacer les caches.
// Déclenché depuis la page de login, ce qui couvre aussi l'expiration de session.
function purgeCaches() {
    navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
            registration.active.postMessage({ type: 'PURGE_CACHES' });
        }
    });
}

function registerServiceWorker() {
    const swUrl = meta('pwa-service-worker');
    const scope = meta('pwa-scope');

    if (!('serviceWorker' in navigator) || !swUrl) {
        return;
    }

    navigator.serviceWorker
        .register(swUrl, scope ? { scope } : undefined)
        .then(() => {
            if (meta('pwa-purge-caches') === '1') {
                purgeCaches();
            }
        })
        .catch(() => {
            // L'Espace Famille reste utilisable sans PWA.
        });
}

// Écoute posée au plus tôt : Chrome peut émettre l'événement avant le
// DOMContentLoaded. `preventDefault` confisque sa bannière native.
window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    showBanner(ANDROID_CLASS);
});

// Masquer ne suffit pas : l'événement continue d'être émis après certaines
// installations, et le bandeau reviendrait à chaque chargement.
window.addEventListener('appinstalled', () => {
    markInstalled(true);
    hideBanner();
});

document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
        return;
    }

    if (event.target.closest('[data-pwa-install-close]')) {
        dismiss();
        hideBanner();

        return;
    }

    if (event.target.closest('[data-pwa-install-button]') && deferredPrompt) {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.finally(() => {
            deferredPrompt = null;
            hideBanner();
        });
    }
});

registerServiceWorker();
syncInstalledState();

// Sur iOS, ni `appinstalled` ni `getInstalledRelatedApps` n'existent : le mode
// standalone est la seule preuve qu'on ait que l'application est installée.
if (isStandalone()) {
    markInstalled(true);
}

if (isIosSafari()) {
    showBanner(IOS_CLASS);
}
